import {
	getConsentParameters,
	getTrackingParameters,
	trackFormSuccess,
} from "../../../../js/utils/helper/analytics.js";
import { doFetch } from "../../../../js/utils/helper/fetch.js";
import { validateInformationRequestForm } from "../../../../js/utils/helper/validators.js";
import {
	displayErrors,
	getFormattedConsentFields,
	getFormTokenHeaders,
	scheduleFormTokenRefresh,
	hideAllErrors,
	initBaseForm,
	processPhoneField,
} from "../../../../js/utils/helper/forms.js";

/**
 * Maps a `contactType` radio value to the human-readable English `lead_type`
 * label reported to GA4. Every value in `InformationRequestContactTypeSchema`
 * must have an entry here — `analytics.test.js` guards that, because a missing
 * entry silently drops `lead_type` from the `form_success` payload rather than
 * failing loudly.
 *
 * These labels intentionally differ from the dealer request form's labels for
 * the same keys ("Home Visit" vs "On-Site Appointment"): the two forms report
 * separate, documented sets of lead types to Finstral's analytics.
 */
export const LEAD_TYPE_LABELS = Object.freeze({
	"on-site-appointment": "Home Visit",
	"showroom-appointment": "Studio Appointment",
	materials: "Information Material",
});

/**
 * `form_success` parameter → form field name, for the categorical fields
 * reported to GA4 (see `TRACKING_PARAMETERS` in analytics-parameters.js).
 * The day/time boxes are unchecked for information-material requests before
 * this is read, so those requests report no weekdays or time of day.
 */
const TRACKED_FIELDS = Object.freeze({
	contact_type: "contactType",
	weekdays: "preferredDay",
	time_of_day: "preferredTime",
	additional_materials: "materials",
});

export class InformationRequestForm extends HTMLElement {
	static #selectors = {
		contactTypeFieldset: ".js-ContactType",
		dayTimeFieldset: "#day-time",
	};

	/** Contact type values that hide the day/time section. */
	static #noDayTimeContactTypes = ["materials"];

	#isInformationMaterialRequest = false;
	/** @type {AbortController | null} */
	#abortController = null;

	/**
	 * Retrieves the elements based on the defined selectors.
	 * @returns {object} An object containing the selected elements.
	 */
	#getElements() {
		return {
			contactTypeFieldset: this.querySelector(
				InformationRequestForm.#selectors.contactTypeFieldset,
			),
			dayTimeFieldset: this.querySelector(
				InformationRequestForm.#selectors.dayTimeFieldset,
			),
		};
	}

	#elements;

	constructor() {
		super();
	}

	connectedCallback() {
		this.#abortController = new AbortController();
		this.#elements = this.#getElements();
		this.#addEventListeners(this.#abortController.signal);
	}

	disconnectedCallback() {
		this.#abortController?.abort();
	}

	/**
	 * Formats form data into a structured object.
	 * @param {FormData} formData - The form data to format.
	 * @param {boolean} withDayTime - Whether to include day/time preferences. [default: true]
	 * @returns {object} The formatted data object.
	 */
	#formatData(formData, withDayTime = true) {
		let constructedData = Object.fromEntries(formData);

		if (withDayTime) {
			constructedData.preferredDay = formData.getAll("preferredDay");
			constructedData.preferredTime = formData.getAll("preferredTime");
		}

		constructedData.materials = [];
		if (Object.hasOwn(constructedData, "materials")) {
			constructedData.materials = formData.getAll("materials");
		}

		constructedData = processPhoneField(
			constructedData.phone,
			constructedData,
			/** minAllowedLength */ 5,
		);

		const consent = getFormattedConsentFields(constructedData);

		constructedData = {
			...constructedData,
			...consent,
		};

		return constructedData;
	}

	/**
	 * Handles the form submission event, validates the form, displays errors, and submits the form data.
	 * @param {Event} event - The form submission event.
	 */
	async #handleFormSubmit(event) {
		event.preventDefault();

		const form = this.querySelector("form");
		const successUrl = form.dataset.success;
		hideAllErrors(this);

		const submitButton = form.querySelector("button[type='submit']");
		submitButton.classList.toggle("Button--loading");
		submitButton.disabled = true;

		// deselects all day time options before submitting when day time should not be available.
		const { dayTimeFieldset } = this.#elements;
		let validationSchema;
		if (this.#isInformationMaterialRequest) {
			validationSchema = "noDayTime";
			const options = Array.from(
				dayTimeFieldset.querySelectorAll("[type='checkbox']"),
			);

			options.forEach((option) => {
				if (option.checked) {
					option.checked = false;
				}
			});
		}

		const formData = new FormData(form);
		// Read the contact type from the submitted data rather than tracking it
		// in a field updated by the `change` listener: the first radio is
		// pre-checked in markup (see form-section/contact-type), so a user who
		// accepts the default never fires a `change` event.
		const contactType = String(formData.get("contactType") ?? "");
		const formatted = this.#formatData(
			formData,
			!this.#isInformationMaterialRequest,
		);

		const validation = validateInformationRequestForm(
			formatted,
			validationSchema,
		);

		if (!validation.success) {
			displayErrors(validation.errors, this);
			submitButton.classList.toggle("Button--loading");
			submitButton.disabled = false;
			return;
		}

		const additionalData = document.querySelector("#additional-form-data");
		if (additionalData) {
			try {
				formatted.additionalData = JSON.parse(additionalData.textContent);
			} catch (error) {
				throw new Error("Invalid JSON in additional data", { cause: error });
			}
		}

		const serverResponse = await doFetch(formatted, form.action, {
			headers: getFormTokenHeaders(),
		});

		const redirectUrl = serverResponse?.redirect_url || successUrl;
		if (serverResponse && redirectUrl) {
			trackFormSuccess({
				form: "information-request-form",
				leadType: LEAD_TYPE_LABELS[contactType],
				formDestination: redirectUrl,
				parameters: {
					...getConsentParameters(form),
					...getTrackingParameters(form, TRACKED_FIELDS),
				},
			});
			window.location.assign(redirectUrl);
		} else {
			submitButton.classList.toggle("Button--loading");
			submitButton.disabled = false;
		}
	}

	/**
	 * Adds event listeners to information request form.
	 */
	/**
	 * @param {AbortSignal} signal
	 */
	#addEventListeners(signal) {
		const { dayTimeFieldset } = this.#elements;

		// we will handle validation manually, so we disable the browser's default validation.
		const form = this.querySelector("form");
		form.setAttribute("novalidate", "novalidate");

		this.addEventListener(
			"change",
			(event) => {
				const target = /** @type {HTMLInputElement} */ (event.target);

				// Only process contact type radio changes
				if (target.name !== "contactType") {
					return;
				}

				// Hide day time options when selecting contact types that don't need appointments
				const shouldHideDayTime =
					InformationRequestForm.#noDayTimeContactTypes.includes(target.value);

				dayTimeFieldset.hidden = shouldHideDayTime;
				this.#isInformationMaterialRequest = shouldHideDayTime;
			},
			{ signal },
		);

		initBaseForm(form, {
			hasNationality: false,
			hasPhoneNumber: true,
			hasProvince: true,
		});

		this.addEventListener("focusin", scheduleFormTokenRefresh, {
			once: true,
			signal,
		});
		this.addEventListener("submit", this.#handleFormSubmit, { signal });
	}
}

customElements.define("information-request-form", InformationRequestForm);
